/**
 * AI assistants (MCP): the contract between the UnfoldWIN app, the backend and the
 * UnfoldWIN connector that Claude and ChatGPT use.
 *
 * The connector is a remote MCP server. An assistant connects through UnfoldWIN's own
 * OAuth sign-in, sees open Next Steps, and asks the person before every change. What each
 * assistant may do is a ladder of permissions the person sets per assistant in
 * Settings → AI assistants:
 *
 *   1 Steps          always on    list_next_steps, get_next_step
 *   2 Actions        on           complete_next_step, add_step_note, create_unfolding_draft
 *     Calendar       on           schedule_next_step, find_free_times (needs Google Calendar)
 *   3 Journal        off          read_journal_entry (summaries or full, 24 hours or until off)
 *   4 Suggest steps  off          create_next_step
 *
 * Everything an assistant changes is logged as activity and marked on the step
 * ("Done with Claude"). Step text, notes and journals are the person's own words: the
 * connector treats them as data and never follows instructions found inside them.
 *
 * One file, no runtime dependencies: the app (Vite), the connector (Node) and the tests
 * (`node --test`) all import it as-is.
 */

// ── Assistants ───────────────────────────────────────────────────────────────

export const ASSISTANT_IDS = ["claude", "chatgpt"] as const;
export type AssistantId = (typeof ASSISTANT_IDS)[number];

export interface ConnectStep {
  title: string;
  detail: string;
}

export interface AssistantInfo {
  id: AssistantId;
  name: string;
  /** The letter on the assistant's colour square. */
  mark: string;
  /** Where the person adds a custom connector. */
  settingsUrl: string;
  /** Steps 2 and 3 of the Connect dialog; step 1 is always "Copy your connector link". */
  connectSteps: [ConnectStep, ConnectStep];
  /** Which of the assistant's own plans support custom connectors. */
  planNote: string;
}

export const ASSISTANTS: Record<AssistantId, AssistantInfo> = {
  claude: {
    id: "claude",
    name: "Claude",
    mark: "C",
    settingsUrl: "https://claude.ai/settings/connectors",
    connectSteps: [
      {
        title: "In Claude, open Settings → Connectors → Add custom connector",
        detail: "Paste the link and name it UnfoldWIN.",
      },
      { title: "Sign in to UnfoldWIN when Claude asks", detail: "You will choose what Claude can see and do." },
    ],
    planNote:
      "Custom connectors are available on Claude Pro, Max, Team and Enterprise. On Team and Enterprise, an owner adds the connector first.",
  },
  chatgpt: {
    id: "chatgpt",
    name: "ChatGPT",
    mark: "G",
    settingsUrl: "https://chatgpt.com/#settings/Connectors",
    connectSteps: [
      {
        title: "In ChatGPT, open Settings → Apps → Advanced settings → Developer mode",
        detail: "Then Create app, paste the link and name it UnfoldWIN.",
      },
      {
        title: "Sign in to UnfoldWIN when ChatGPT asks",
        detail: "You will choose what ChatGPT can see and do. In a chat, pick UnfoldWIN from the + menu.",
      },
    ],
    planNote:
      "Developer mode is in beta and web only, on ChatGPT Plus, Pro, Business, Enterprise and Education. A listing in ChatGPT's app directory would make this one click.",
  },
};

export function isAssistantId(value: unknown): value is AssistantId {
  return typeof value === "string" && (ASSISTANT_IDS as readonly string[]).includes(value);
}

/** The production connector link people paste into their assistant. */
export const DEFAULT_CONNECTOR_URL = "https://mcp.unfoldwin.com/mcp";

// ── Permissions ──────────────────────────────────────────────────────────────

export type JournalMode = "summaries" | "full";
export type JournalDuration = "24h" | "until_off";

export interface JournalAccess {
  enabled: boolean;
  mode: JournalMode;
  duration: JournalDuration;
  /** When access switches itself off. Null while off, or when it lasts until turned off. */
  expiresAt: string | null;
}

/** Rung 1 (see open Next Steps) is always on, so it isn't stored. */
export interface AssistantPermissions {
  /** Tick done, add notes, send a thought. The assistant still asks before each one. */
  actions: boolean;
  /** Schedule steps and look up free times on the connected Google Calendar. */
  calendar: boolean;
  journal: JournalAccess;
  /** create_next_step: new steps marked "Suggested with …". */
  suggestSteps: boolean;
}

export const DEFAULT_PERMISSIONS: AssistantPermissions = {
  actions: true,
  calendar: true,
  journal: { enabled: false, mode: "summaries", duration: "24h", expiresAt: null },
  suggestSteps: false,
};

/** Body of `PATCH /api/assistants/:assistant/permissions`. The server sets `expiresAt`. */
export interface PermissionsUpdate {
  actions?: boolean;
  calendar?: boolean;
  journal?: { enabled: boolean; mode: JournalMode; duration: JournalDuration };
  suggestSteps?: boolean;
}

export const JOURNAL_ACCESS_HOURS = 24;

export function journalExpiry(duration: JournalDuration, now: Date): string | null {
  return duration === "24h" ? new Date(now.getTime() + JOURNAL_ACCESS_HOURS * 3_600_000).toISOString() : null;
}

/** On, and not past its 24 hours. */
export function journalAccessActive(journal: JournalAccess, now: Date): boolean {
  if (!journal.enabled) return false;
  return !journal.expiresAt || new Date(journal.expiresAt).getTime() > now.getTime();
}

// ── Connections ──────────────────────────────────────────────────────────────

/** `paused`: connected, but the plan no longer includes assistants (tools refuse until it does). */
export type ConnectionStatus = "not_connected" | "connected" | "paused";

export interface AssistantConnection {
  assistant: AssistantId;
  status: ConnectionStatus;
  connectedAt: string | null;
  lastUsedAt: string | null;
  permissions: AssistantPermissions;
}

/** `GET /api/assistants`. */
export interface AssistantsOverview {
  /** The person's plan includes AI assistants (Plus and Premium). */
  included: boolean;
  connectorUrl: string;
  /** A Google Calendar is connected; scheduling and free times need one. */
  calendarConnected: boolean;
  connections: AssistantConnection[];
  lastActivityAt: string | null;
}

// ── Tools ────────────────────────────────────────────────────────────────────

export type ToolName =
  | "list_next_steps"
  | "get_next_step"
  | "find_free_times"
  | "complete_next_step"
  | "add_step_note"
  | "schedule_next_step"
  | "create_unfolding_draft"
  | "read_journal_entry"
  | "create_next_step";

export type Rung = "steps" | "actions" | "calendar" | "journal" | "suggest";

export interface ToolInfo {
  rung: Rung;
  /** Changes data. Assistants ask the person before running these. */
  writes: boolean;
}

export const TOOLS: Record<ToolName, ToolInfo> = {
  list_next_steps: { rung: "steps", writes: false },
  get_next_step: { rung: "steps", writes: false },
  find_free_times: { rung: "calendar", writes: false },
  complete_next_step: { rung: "actions", writes: true },
  add_step_note: { rung: "actions", writes: true },
  create_unfolding_draft: { rung: "actions", writes: true },
  schedule_next_step: { rung: "calendar", writes: true },
  read_journal_entry: { rung: "journal", writes: false },
  create_next_step: { rung: "suggest", writes: true },
};

export type AccessDenial =
  | "plan_required"
  | "not_connected"
  | "actions_off"
  | "calendar_off"
  | "calendar_not_connected"
  | "journal_off"
  | "journal_expired"
  | "suggest_off";

export interface AccessContext {
  included: boolean;
  calendarConnected: boolean;
  connection: AssistantConnection;
  now: Date;
}

/**
 * Whether a tool may run right now. Checked on every call against the live connection,
 * not the token's scopes, so a change in Settings applies to the very next call.
 */
export function toolAccess(tool: ToolName, ctx: AccessContext): { ok: true } | { ok: false; reason: AccessDenial } {
  const { connection, now } = ctx;
  if (connection.status === "not_connected") return { ok: false, reason: "not_connected" };
  if (!ctx.included || connection.status === "paused") return { ok: false, reason: "plan_required" };
  const p = connection.permissions;
  switch (TOOLS[tool].rung) {
    case "steps":
      return { ok: true };
    case "actions":
      return p.actions ? { ok: true } : { ok: false, reason: "actions_off" };
    case "calendar":
      if (!p.calendar) return { ok: false, reason: "calendar_off" };
      return ctx.calendarConnected ? { ok: true } : { ok: false, reason: "calendar_not_connected" };
    case "journal":
      if (!p.journal.enabled) return { ok: false, reason: "journal_off" };
      return journalAccessActive(p.journal, now) ? { ok: true } : { ok: false, reason: "journal_expired" };
    case "suggest":
      return p.suggestSteps ? { ok: true } : { ok: false, reason: "suggest_off" };
  }
}

/**
 * What a refused tool tells the assistant: why, and what the person can do. Written for
 * the assistant to relay, so it explains instead of guessing (AI 16).
 */
export function denialMessage(reason: AccessDenial, assistant: AssistantId): string {
  const name = ASSISTANTS[assistant].name;
  const where = `Settings → AI assistants → ${name} in UnfoldWIN`;
  switch (reason) {
    case "plan_required":
      return "AI assistants are part of UnfoldWIN Plus and Premium. The person's current plan doesn't include them; they can upgrade in UnfoldWIN.";
    case "not_connected":
      return `${name} is not connected to this UnfoldWIN account. The person can connect it in Settings → AI assistants.`;
    case "actions_off":
      return `The person has turned off changes for ${name}. You can still read their open Next Steps. To tick steps done or add notes, they can turn on "Tick steps done and add notes" in ${where}.`;
    case "calendar_off":
      return `Calendar access is off for ${name}. The person can turn on "Add steps to your calendar" in ${where}.`;
    case "calendar_not_connected":
      return "No calendar is connected to UnfoldWIN yet. The person can connect Google Calendar in UnfoldWIN Settings → Calendar.";
    case "journal_off":
      return `Journal access is off for ${name}, so you can see Next Steps but not the reflections behind them. Don't guess at their content. If the person wants this, they can turn on "Read your journal entries" in ${where}, choosing summaries or full entries and for how long.`;
    case "journal_expired":
      return `Journal access for ${name} has ended (it was on for 24 hours). The person can turn it on again in ${where}.`;
    case "suggest_off":
      return `Suggesting new steps is off for ${name}. The person can turn on "Suggest new steps" in ${where}. Until then, share ideas in the chat instead.`;
  }
}

// ── Activity ─────────────────────────────────────────────────────────────────

export type ActivityKind =
  | "completed"
  | "reopened"
  | "note_added"
  | "scheduled"
  | "step_suggested"
  | "thought_sent"
  | "journal_read"
  | "journal_access_ended"
  | "connected"
  | "disconnected";

export interface ActivityStep {
  id: string;
  journalId: string;
  text: string;
}

export interface ActivityEvent {
  id: string;
  assistant: AssistantId;
  kind: ActivityKind;
  at: string;
  step?: ActivityStep;
  /** `scheduled`: the event's time. */
  start?: string;
  end?: string;
  /** `step_suggested`: the step's month, "yyyy-MM". */
  month?: string;
  /** `journal_read`: how many entries, and when access ends (null: until turned off). */
  count?: number;
  mode?: JournalMode;
  accessEndsAt?: string | null;
}

/** `GET /api/assistants/activity?after=<iso>&limit=<n>`, newest first. */
export interface ActivityPage {
  events: ActivityEvent[];
}

/** Changes worth a "back in UnfoldWIN" toast. Reads and connection events aren't. */
const TOAST_KINDS = new Set<ActivityKind>(["completed", "note_added", "scheduled", "step_suggested", "thought_sent"]);

export function activityText(event: ActivityEvent, now: Date = new Date()): string {
  const text = event.step ? quote(event.step.text) : "a step";
  switch (event.kind) {
    case "completed":
      return `Marked ${text} done`;
    case "reopened":
      return `Marked ${text} not done`;
    case "note_added":
      return `Added a note to ${text}`;
    case "scheduled":
      return event.start ? `Scheduled ${text} for ${eventWhen(new Date(event.start), now)}` : `Scheduled ${text}`;
    case "step_suggested":
      return `Added ${text}`;
    case "thought_sent":
      return "Sent a thought to wait on Home";
    case "journal_read": {
      const n = event.count ?? 1;
      const what =
        event.mode === "full" ? `${n} full journal ${n === 1 ? "entry" : "entries"}` : `${n} journal ${n === 1 ? "summary" : "summaries"}`;
      const ends = event.accessEndsAt ? ` Access ends ${dayWord(new Date(event.accessEndsAt), now)}, ${formatClock(new Date(event.accessEndsAt))}.` : "";
      return `Read ${what}.${ends}`;
    }
    case "journal_access_ended":
      return "Journal access ended";
    case "connected":
      return "Connected to UnfoldWIN";
    case "disconnected":
      return "Disconnected";
  }
}

/** "Today, 9:14 AM · Claude" */
export function activityMeta(event: ActivityEvent, now: Date = new Date()): string {
  const at = new Date(event.at);
  return `${capitalize(dayWord(at, now))}, ${formatClock(at)} · ${ASSISTANTS[event.assistant].name}`;
}

/**
 * One line for the toast when someone comes back to UnfoldWIN after an assistant made
 * changes ("Claude added 3 steps to Google Calendar"). `events` are the new ones from a
 * single assistant, any order. Null when there's nothing worth interrupting for.
 */
export function toastMessage(events: ActivityEvent[]): string | null {
  const doing = events.filter((e) => TOAST_KINDS.has(e.kind));
  if (doing.length === 0) return null;
  const name = ASSISTANTS[doing[0].assistant].name;
  const kinds = new Set(doing.map((e) => e.kind));
  const steps = new Map<string, ActivityStep>();
  for (const e of doing) if (e.step) steps.set(e.step.id, e.step);
  const only = (...k: ActivityKind[]) => [...kinds].every((x) => k.includes(x));
  const single = steps.size === 1 ? [...steps.values()][0] : null;

  if (only("scheduled")) {
    return single ? `${name} added ${quote(shorten(single.text))} to Google Calendar` : `${name} added ${steps.size} steps to Google Calendar`;
  }
  if (only("step_suggested")) {
    const month = doing.find((e) => e.month)?.month;
    const where = month ? monthName(month) : "Momentum";
    return single ? `${name} added ${quote(single.text)} to ${where}` : `${name} added ${steps.size} steps to ${where}`;
  }
  if (only("completed", "note_added") && kinds.has("completed")) {
    return single ? `${name} marked ${quote(single.text)} done` : `${name} ticked ${steps.size} steps done`;
  }
  if (only("note_added")) {
    return single ? `${name} added a note to ${quote(shorten(single.text))}` : `${name} added notes to ${steps.size} steps`;
  }
  if (only("note_added", "scheduled") && single) {
    return `${name} saved a note and added ${quote(shorten(single.text))} to Google Calendar`;
  }
  if (only("thought_sent")) {
    return doing.length === 1 ? `${name} sent a thought to Home` : `${name} sent ${doing.length} thoughts to Home`;
  }
  return `${name} made ${doing.length} changes`;
}

// ── Step marks ───────────────────────────────────────────────────────────────

/** Strongest first: a step done with an assistant shows "Done with …" even if it planned it too. */
export type StepMarkKind = "done" | "planned" | "suggested" | "noted";

/** What an assistant did to one step, from `GET /api/assistants/step-marks`. */
export interface StepMark {
  stepId: string;
  journalId: string;
  assistant: AssistantId;
  kind: StepMarkKind;
  /** The step's note when an assistant wrote it (shown in the note popover). */
  note: string | null;
  /** An event the assistant put on the calendar for this step. */
  scheduled: { start: string; end: string } | null;
  at: string;
}

export interface StepMarksPage {
  marks: StepMark[];
}

/** "Planned with Claude · note added"; `compact` (phone sheet): "Done with ChatGPT · note". */
export function stepMarkLabel(mark: StepMark, compact = false): string {
  const name = ASSISTANTS[mark.assistant].name;
  const base =
    mark.kind === "done"
      ? `Done with ${name}`
      : mark.kind === "planned"
        ? `Planned with ${name}`
        : mark.kind === "suggested"
          ? `Suggested with ${name}`
          : `Note from ${name}`;
  return mark.kind !== "noted" && mark.note ? `${base} · ${compact ? "note" : "note added"}` : base;
}

/** The time chip on a step an assistant scheduled: "Wed, Oct 14 · 8:00 AM". */
export function scheduledChipLabel(start: string): string {
  const d = new Date(start);
  return `${formatDay(d)} · ${formatClock(d)}`;
}

// ── Thoughts sent to Home ────────────────────────────────────────────────────

export interface UnfoldingDraft {
  id: string;
  assistant: AssistantId;
  kind: "text" | "link";
  text: string;
  sentAt: string;
}

/** `GET /api/assistants/drafts`: thoughts waiting on Home, newest first, minus snoozed ones. */
export interface DraftsPage {
  drafts: UnfoldingDraft[];
}

/** "Sent from Claude · last night, 11:42 PM" */
export function sentFromLabel(draft: Pick<UnfoldingDraft, "assistant" | "sentAt">, now: Date = new Date()): string {
  const at = new Date(draft.sentAt);
  const yesterday = startOfDay(addDays(now, -1));
  let when: string;
  if (sameDay(at, now)) when = `today, ${formatClock(at)}`;
  else if (sameDay(at, yesterday)) when = at.getHours() >= 18 ? `last night, ${formatClock(at)}` : `yesterday, ${formatClock(at)}`;
  else when = `${formatDay(at)}, ${formatClock(at)}`;
  return `Sent from ${ASSISTANTS[draft.assistant].name} · ${when}`;
}

/** "Not now" keeps a thought out of the way until the next morning. */
export function snoozeUntil(now: Date): string {
  const next = startOfDay(addDays(now, 1));
  next.setHours(6);
  return next.toISOString();
}

// ── Consent (OAuth) ──────────────────────────────────────────────────────────

/** `GET /api/oauth/requests/:id`: an assistant asking to connect, shown on the consent page. */
export interface ConsentRequest {
  id: string;
  /** Null when the app isn't Claude or ChatGPT; those can't connect yet. */
  assistant: AssistantId | null;
  clientName: string;
  expiresAt: string;
  included: boolean;
  calendarConnected: boolean;
}

/** Body of `POST /api/oauth/requests/:id/approve`. Steps and actions are always granted. */
export interface ConsentDecision {
  calendar: boolean;
  journal: boolean;
}

/** Both approve and deny answer with where to send the browser back to the assistant. */
export interface ConsentResult {
  redirectTo: string;
}

// ── Hand-off ─────────────────────────────────────────────────────────────────

/** The starter message for "Ask Claude to help" (AI 4 → AI 5). */
export function handoffMessage(stepText: string): string {
  return `Help me with "${stepText.trim().replace(/[.!]+$/, "")}" from UnfoldWIN.`;
}

/** Opens a new chat with the starter message filled in. */
export function handoffUrl(assistant: AssistantId, stepText: string): string {
  const q = encodeURIComponent(handoffMessage(stepText));
  return assistant === "claude" ? `https://claude.ai/new?q=${q}` : `https://chatgpt.com/?q=${q}`;
}

// ── Journal access preview ───────────────────────────────────────────────────

/**
 * The preview under "Read your journal entries" (AI 17): exactly what the assistant will
 * see. Journal access covers the current month's Unfoldings.
 */
export function journalPreview(opts: {
  assistant: AssistantId;
  count: number;
  month: string;
  mode: JournalMode;
  duration: JournalDuration;
  now: Date;
  /** When access that's already running ends; otherwise 24 hours from `now`. */
  endsAt?: string | null;
}): string {
  const name = ASSISTANTS[opts.assistant].name;
  const monthLabel = monthName(opts.month);
  const what =
    opts.count === 0
      ? `There are no ${monthLabel} Unfoldings yet. ${name} will see new ones as you write them`
      : `${name} will see ${opts.count} ${monthLabel} ${opts.count === 1 ? "Unfolding" : "Unfoldings"}`;
  const detail =
    opts.mode === "summaries"
      ? opts.count === 0
        ? ", as a title, date and short summary."
        : `: the title, date and a short summary of ${opts.count === 1 ? "it" : "each"}.`
      : opts.count === 0
        ? ", in full."
        : " in full: the reflection, insight and Next Steps.";
  const ends = opts.endsAt ? new Date(opts.endsAt) : new Date(opts.now.getTime() + JOURNAL_ACCESS_HOURS * 3_600_000);
  const lasts =
    opts.duration === "24h" ? ` Access ends ${dayWord(ends, opts.now)} at ${formatClock(ends)}.` : " Access stays on until you turn it off.";
  return `${what}${detail} Not your canvas notes, drawings or attachments.${lasts}`;
}

// ── API paths (relative to the app's `/api`) ─────────────────────────────────

export const ASSISTANT_API = {
  overview: "/assistants",
  permissions: (a: AssistantId) => `/assistants/${a}/permissions`,
  connection: (a: AssistantId) => `/assistants/${a}`,
  activity: "/assistants/activity",
  stepMarks: "/assistants/step-marks",
  drafts: "/assistants/drafts",
  snoozeDraft: (id: string) => `/assistants/drafts/${encodeURIComponent(id)}/snooze`,
  unfoldDraft: (id: string) => `/assistants/drafts/${encodeURIComponent(id)}/unfold`,
  consent: (id: string) => `/oauth/requests/${encodeURIComponent(id)}`,
  approve: (id: string) => `/oauth/requests/${encodeURIComponent(id)}/approve`,
  deny: (id: string) => `/oauth/requests/${encodeURIComponent(id)}/deny`,
} as const;

// ── Dates (local time, like Momentum) ────────────────────────────────────────

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "2026-10" → "October" */
export function monthName(key: string): string {
  return MONTHS_LONG[Number(key.slice(5, 7)) - 1] ?? key;
}

/** "9:14 AM" */
export function formatClock(d: Date): string {
  const h = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() >= 12 ? "PM" : "AM"}`;
}

/** "Wed, Oct 14" */
export function formatDay(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Wed, 8:00 AM" inside the coming week, else "Oct 14, 8:00 AM". */
function eventWhen(d: Date, now: Date): string {
  const days = Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / 86_400_000);
  const day = days >= -6 && days <= 6 ? WEEKDAYS[d.getDay()] : `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return `${day}, ${formatClock(d)}`;
}

/** "today" | "yesterday" | "tomorrow" | "Wed, Oct 14" */
function dayWord(d: Date, now: Date): string {
  if (sameDay(d, now)) return "today";
  if (sameDay(d, addDays(now, -1))) return "yesterday";
  if (sameDay(d, addDays(now, 1))) return "tomorrow";
  return formatDay(d);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function quote(text: string): string {
  return `“${text}”`;
}

/** First four words, for toasts that name a step alongside a second change. */
function shorten(text: string): string {
  const words = text.trim().replace(/[.!]+$/, "").split(/\s+/);
  return words.length > 4 ? `${words.slice(0, 4).join(" ")}…` : words.join(" ");
}
